import { createClient, type SupabaseClient, type SupportedStorage } from '@supabase/supabase-js';
import type { Database } from './database.types';

export type Db = SupabaseClient<Database>;

type Publico = Database['public'];
export type Linha<T extends keyof Publico['Tables']> = Publico['Tables'][T]['Row'];
export type LinhaView<T extends keyof Publico['Views']> = Publico['Views'][T]['Row'];
export type Enum<T extends keyof Publico['Enums']> = Publico['Enums'][T];

export interface ConfigCliente {
  url: string;
  chaveAnon: string;
  /** Onde guardar a sessão. Web: localStorage (padrão). Apps: um storage nativo. */
  armazenamento?: SupportedStorage;
  /** Web lê a sessão da URL (links de e-mail); apps não. */
  sessaoNaUrl?: boolean;
  /**
   * Login pelo navegador volta com um código (PKCE) que o próprio app troca pela sessão: é o jeito
   * seguro no celular. O painel web segue no fluxo padrão.
   */
  fluxo?: 'pkce' | 'implicit';
  /**
   * Requisição sem resposta depois disto vira "sem conexão". Sinal ruim (conectado, mas nada passa)
   * deixava a tela carregando sem fim: o Android não corta sozinho.
   */
  prazoMs?: number;
}

/**
 * O fetch com prazo. Esgotado, falha como o fetch do React Native sem sinal ("Network request timed
 * out"): o resto do caminho (postgrest-js, auth-js, traduzirErro) já trata isso como falta de rede.
 * Cancelamento de quem chamou (o React Query desistindo) continua sendo cancelamento.
 */
export function fetchComPrazo(prazoMs: number, base: typeof fetch = fetch): typeof fetch {
  return (entrada, opcoes) => {
    const controle = new AbortController();
    const externo = opcoes?.signal;
    if (externo?.aborted) controle.abort(externo.reason);
    else externo?.addEventListener('abort', () => controle.abort(externo.reason), { once: true });
    let esgotou = false;
    const prazo = setTimeout(() => {
      esgotou = true;
      controle.abort();
    }, prazoMs);
    return base(entrada, { ...opcoes, signal: controle.signal })
      .catch((e: unknown) => {
        throw esgotou ? new TypeError('Network request timed out') : e;
      })
      .finally(() => clearTimeout(prazo));
  };
}

/**
 * Cria o cliente Supabase tipado. Cada app cria UM e entrega via <ProvedorApi>.
 * A chave é a pública (anon): quem protege os dados é o RLS do banco.
 */
export function criarClienteSupabase(config: ConfigCliente): Db {
  if (!config.url || !config.chaveAnon) {
    throw new Error('Configuração do Supabase ausente: defina a URL e a chave pública do projeto.');
  }
  return createClient<Database>(config.url, config.chaveAnon, {
    auth: {
      ...(config.armazenamento ? { storage: config.armazenamento } : {}),
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: config.sessaoNaUrl ?? false,
      flowType: config.fluxo ?? 'implicit',
    },
    // Quem repete sem sinal é o React Query (2 vezes, igual para leitura e função do banco). Com a
    // repetição própria do postgrest-js (só em GET, 3 vezes) somada, uma tela sem cache levava ~24 s
    // para dizer "Sem conexão".
    db: { retry: false },
    ...(config.prazoMs ? { global: { fetch: fetchComPrazo(config.prazoMs) } } : {}),
  });
}
